import React from "react";
import { Modal, Tag } from "antd";
import { round } from "lodash";
import pluralize from "pluralize";
import {
  describeReturnTier,
  metersToMiles,
  resolveReturnDistanceTier,
  RETURN_TIER
} from "helper/returnPricing";
import "./PricingDetailsModal.scss";

/**
 * Return to pickup / return failed packages are compliance-only flags - they never gate the
 * return-distance charge, so always render the discrete value actually in effect for this
 * order rather than an ambiguous "client default" placeholder.
 */
const FlagTag = ({ value }) => (
  <Tag color={value ? "green" : "default"}>{value ? "On" : "Off"}</Tag>
);

/**
 * By contract the return-distance rate is always billed, subject to the Free / Short / Long
 * tiers - so "billed" here means the resolved tier isn't the free one, not whether either
 * return-process toggle is on. Matches the indicator on the Admin Route Detail panel.
 */
const BilledTag = ({ billed }) => (
  <Tag color={billed ? "green" : "red"}>{billed ? "Y" : "No (under free threshold)"}</Tag>
);

const MetricRow = ({ label, value }) => (
  <div className="pricing-item">
    <span className="item-label">{label}</span>
    <span className="item-value">{value}</span>
  </div>
);

const PricingDetailsModal = ({
  visible,
  onClose,
  routeEstimate,
  routeName,
  rate,
  route,
  returnToPickupEnabled,
  returnOnFailedEnabled
}) => {
  if (!routeEstimate) return null;

  const returnMiles = Number(routeEstimate.totalReturnDistance) || 0;
  const returnHours = Number(routeEstimate.totalReturnTime) || 0;
  const returnTier = rate ? resolveReturnDistanceTier(returnMiles, rate) : null;
  const tierLabel = (tier) => (rate ? ` — ${describeReturnTier(tier, rate)}` : "");
  const isReturnBilled = returnTier
    ? returnTier.tier !== RETURN_TIER.NONE
    : returnMiles > 0;

  const travelMins = route?.travelMins || 0;
  const serviceMins = route?.serviceMins || 0;
  const waitingMins = route?.waitingMins || 0;
  const workingMins = route?.workingMins || 0;
  const workingPercentage = route?.workingPercentage || 0;
  const distanceKm = route?.distance ? round(route.distance / 1000, 2) : 0;
  const calculatedReturnMiles = round(metersToMiles(route?.returnDistance), 2);
  const calculatedReturnKm = route?.returnDistance
    ? round(route.returnDistance / 1000, 2)
    : 0;
  const calculatedReturnMins = route?.returnMins || 0;

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return "$0.00";
    return `$${Number(value).toFixed(2)}`;
  };

  const formatNumber = (value, decimals = 2) => {
    if (value === undefined || value === null) return "0";
    return Number(value).toFixed(decimals);
  };

  const renderSection = (title, items) => {
    const hasValues = items.some(
      (item) => item.value !== 0 && item.value !== null && item.value !== undefined
    );
    if (!hasValues) return null;

    return (
      <div className="pricing-section">
        <h3 className="section-title">{title}</h3>
        <div className="pricing-items">
          {items.map((item, index) => {
            if (item.value === 0 || item.value === null || item.value === undefined)
              return null;
            return (
              <div key={index} className="pricing-item">
                <span className="item-label">{item.label}</span>
                <span className="item-value">
                  {item.isCurrency ? formatCurrency(item.value) : item.value}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={`Pricing Details - ${routeName || "Route"}`}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      className="pricing-details-modal"
    >
      <div className="pricing-details-content">
        {/* Route Metrics */}
        {route && (
          <div className="pricing-section">
            <h3 className="section-title">Route Metrics</h3>
            <div className="pricing-items pricing-metrics-grid">
              <MetricRow
                label="Travel Time"
                value={`${travelMins} ${pluralize("min", travelMins)}`}
              />
              <MetricRow
                label="Service Time"
                value={`${serviceMins} ${pluralize("min", serviceMins)}`}
              />
              <MetricRow
                label="Waiting Time"
                value={`${waitingMins} ${pluralize("min", waitingMins)}`}
              />
              <MetricRow
                label="Working Time"
                value={`${workingMins} ${pluralize("min", workingMins)}`}
              />
              <MetricRow label="Working Percentage" value={`${workingPercentage}%`} />
              <MetricRow label="Distance" value={`${distanceKm} km`} />
            </div>
          </div>
        )}

        {/* Return Process */}
        <div className="pricing-section">
          <h3 className="section-title">Return Process</h3>
          <div className="pricing-items pricing-metrics-grid">
            <MetricRow
              label="Return to pickup after all stops"
              value={<FlagTag value={returnToPickupEnabled} />}
            />
            <MetricRow
              label="Return failed packages"
              value={<FlagTag value={returnOnFailedEnabled} />}
            />
            <MetricRow
              label="Calculated Return Distance"
              value={`${calculatedReturnMiles} mi (${calculatedReturnKm} km)`}
            />
            <MetricRow label="Billed?" value={<BilledTag billed={isReturnBilled} />} />
            <MetricRow
              label="Return Time"
              value={`${calculatedReturnMins} ${pluralize("min", calculatedReturnMins)}`}
            />
          </div>
        </div>

        {/* Base & Time Components */}
        {renderSection("Base & Time-Based Charges", [
          { label: "Base Price", value: routeEstimate.basePrice, isCurrency: true },
          {
            label: `Additional Time on Route (${formatNumber(
              routeEstimate.additionalTimeOnRoute
            )} hrs)`,
            value: routeEstimate.priceAdditionalTimeOnRoute,
            isCurrency: true
          },
          {
            label: `Return Time (${formatNumber(returnHours)} hrs)`,
            value: routeEstimate.priceReturnTime,
            isCurrency: true
          }
        ])}

        {/* Distance Components */}
        {renderSection("Distance-Based Charges", [
          {
            label: "Distance on Route",
            value: routeEstimate.priceDistanceOnRoute,
            isCurrency: true
          },
          {
            label: `Return Distance${tierLabel(RETURN_TIER.SHORT)}`,
            value: routeEstimate.priceReturnDistanceShort,
            isCurrency: true
          },
          {
            label: `Return Distance${tierLabel(RETURN_TIER.LONG)}`,
            value: routeEstimate.priceReturnDistanceLong,
            isCurrency: true
          },
          {
            label: `Fuel Surcharge (${formatNumber(routeEstimate.fuelSurcharge)} mi)`,
            value: routeEstimate.priceFuelSurcharge,
            isCurrency: true
          }
        ])}

        {/* Package Components */}
        {renderSection("Package Charges", [
          {
            label: "Postal Packages",
            value: routeEstimate.pricePostalPackage,
            isCurrency: true
          },
          {
            label: "Micro Packages",
            value: routeEstimate.priceMicroPackage,
            isCurrency: true
          },
          {
            label: "Small Packages",
            value: routeEstimate.priceSmallPackage,
            isCurrency: true
          },
          {
            label: "Medium Packages",
            value: routeEstimate.priceMediumPackage,
            isCurrency: true
          },
          {
            label: "Large Packages",
            value: routeEstimate.priceLargePackage,
            isCurrency: true
          },
          {
            label: "Custom Packages",
            value: routeEstimate.priceCustomPackage,
            isCurrency: true
          },
          {
            label: "Pallet Packages",
            value: routeEstimate.pricePalletPackage,
            isCurrency: true
          },
          {
            label: "Total Package Charges",
            value: routeEstimate.priceTotalPackages,
            isCurrency: true
          }
        ])}

        {/* Stop Components */}
        {renderSection("Stop Charges", [
          {
            label: "Pickup Stops",
            value: routeEstimate.pricePickupStops,
            isCurrency: true
          },
          {
            label: "Delivery Stops",
            value: routeEstimate.priceDeliveryStops,
            isCurrency: true
          },
          {
            label: `Return Delivery Stop (${routeEstimate.returnDeliveryStop || 0})`,
            value: routeEstimate.priceReturnDeliveryStop,
            isCurrency: true
          },
          {
            label: `Over Delivery Stops (${routeEstimate.overDeliveryStops || 0} extra)`,
            value: routeEstimate.priceOverDeliveryStops,
            isCurrency: true
          }
        ])}

        {/* Discounts & Minimums */}
        {renderSection("Discounts & Guarantees", [
          {
            label: "Volume Package Discount",
            value: routeEstimate.volumePackageDiscounts,
            isCurrency: true
          },
          {
            label: "Minimum Deliveries Charge",
            value: routeEstimate.priceMinimumDeliveries,
            isCurrency: true
          },
          {
            label: "Minimum Earnings Guarantee",
            value: routeEstimate.priceMinimumEarnings,
            isCurrency: true
          }
        ])}

        {/* Subtotal */}
        <div className="pricing-section pricing-subtotal">
          <div className="pricing-item">
            <span className="item-label">
              <strong>Pre-Subtotal</strong>
            </span>
            <span className="item-value">
              <strong>{formatCurrency(routeEstimate.preSubTotal)}</strong>
            </span>
          </div>
          <div className="pricing-item">
            <span className="item-label">
              <strong>Subtotal (after minimums)</strong>
            </span>
            <span className="item-value">
              <strong>{formatCurrency(routeEstimate.subTotal)}</strong>
            </span>
          </div>
        </div>

        {/* Fees & Taxes */}
        <div className="pricing-section pricing-final">
          <h3 className="section-title">Fees & Taxes</h3>
          <div className="pricing-items">
            <div className="pricing-item">
              <span className="item-label">Booking Fee</span>
              <span className="item-value">
                {formatCurrency(routeEstimate.bookingFee)}
              </span>
            </div>
            {routeEstimate.priceAdditionalInsurance > 0 && (
              <div className="pricing-item">
                <span className="item-label">Additional Insurance</span>
                <span className="item-value">
                  {formatCurrency(routeEstimate.priceAdditionalInsurance)}
                </span>
              </div>
            )}
            {routeEstimate.priceB2BTax > 0 && (
              <div className="pricing-item">
                <span className="item-label">B2B Tax (4%)</span>
                <span className="item-value">
                  {formatCurrency(routeEstimate.priceB2BTax)}
                </span>
              </div>
            )}
            {routeEstimate.priceSalesTax > 0 && (
              <div className="pricing-item">
                <span className="item-label">Sales Tax (11.5%)</span>
                <span className="item-value">
                  {formatCurrency(routeEstimate.priceSalesTax)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Grand Total */}
        <div className="pricing-section pricing-total">
          <div className="pricing-item">
            <span className="item-label">
              <strong>TOTAL ESTIMATE</strong>
            </span>
            <span className="item-value total-price">
              <strong>{formatCurrency(routeEstimate.estimatePrice)}</strong>
            </span>
          </div>
        </div>

        {/* Info Note */}
        <div className="pricing-note">
          <p>
            <strong>Note:</strong> This breakdown shows all the components that make up
            the estimated price. The actual final price may differ based on route
            completion time, waiting times, and delivery success rate.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default PricingDetailsModal;
